import { useContext, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { MediaLibrary } from '../../builder/media/MediaLibrary';

/**
 * MediaPickerField — choisir (ou charger) une image de la médiathèque depuis un
 * formulaire YAML, et n'en stocker que l'identifiant du média.
 *
 * Pourquoi du JS et pas du YAML : charger un fichier est un envoi MULTIPART.
 * Le `FileField` du noyau reactive-json n'est qu'un `<input type="file">` dont
 * la valeur stockée est celle de l'input (`C:\fakepath\icone.png`) — aucun
 * octet ne part —, et le pattern DataSync poste du JSON. Aucune brique
 * déclarative ne couvre donc « charger une image ». Ce champ n'ajoute pas un
 * second téléversement : il MONTE la médiathèque qui existe déjà (grille,
 * dépôt, catégories, rattachement au site, `/api/builder/media/upload`) et
 * écrit dans le store ce que le back attend d'un champ de référence — l'id du
 * média, comme un `SelectField` écrit un nid.
 *
 * Props YAML :
 *   dataLocation – liaison de formulaire standard (reçoit l'id du média, ou '')
 *   previewUrl   – adresse de l'image DÉJÀ enregistrée, pour l'aperçu initial
 *                  (l'entité ne transporte que l'id ; le back sert l'adresse à
 *                  côté, cf. `faviconUrl`)
 *   siteId       – site sur lequel ouvrir la médiathèque et auquel rattacher ce
 *                  qui est chargé (hors du builder, aucun contexte ne le dit)
 *   siteName     – son nom, pour l'intitulé du rail « site » de la pop-in
 *   title        – titre de la pop-in (défaut « Médiathèque »)
 *   buttonLabel  – libellé du bouton quand rien n'est choisi
 *   emptyText    – texte affiché à la place de l'aperçu quand rien n'est choisi
 *   previewAlt   – alternative de l'aperçu (l'image EST la valeur du champ :
 *                  la déclarer décorative priverait un lecteur d'écran du seul
 *                  rendu de l'état « une image est choisie »)
 *   inputId      – id porté par le bouton
 *   labelledBy   – id du libellé du champ. Un `<label for>` ne nomme PAS un
 *                  bouton (HTML-AAM) : le nom accessible se compose ici, du
 *                  libellé PUIS du texte du bouton (« Favicone Remplacer »).
 *   describedBy  – id du texte d'aide, pour qu'il soit restitué avec le champ
 *
 * Exemple :
 *   - type: MediaPickerField
 *     dataLocation: ~~.siteForm.data.favicon
 *     previewUrl: ~~.formFaviconUrl
 *     siteId: ~~.site.data.id
 */
export const MediaPickerField = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const resolve = (valueToEvaluate) =>
        evaluateTemplateValue({ valueToEvaluate, globalDataContext, templateContext });

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: '',
        globalDataContext,
        templateContext,
    });

    const [open, setOpen] = useState(false);
    // L'adresse de ce qui vient d'être choisi. Le store ne porte que l'id : sans
    // elle, l'aperçu resterait celui de l'image précédente jusqu'au rechargement
    // de la page. `mid` retient POUR QUEL média elle vaut — rouvrir la modale
    // sur un autre site change la valeur liée sans repasser par ce composant.
    const [picked, setPicked] = useState(null);

    // `evaluateTemplateValue` rend le CHEMIN BRUT quand il ne résout rien : sans
    // ce filtre, un `previewUrl` mal câblé partirait en `<img src="~~.…">`.
    const text = (value) => {
        const resolved = resolve(value);
        return typeof resolved === 'string' && !resolved.startsWith('~') ? resolved : '';
    };

    const value = formData === null || formData === undefined ? '' : String(formData);
    const previewUrl = picked && String(picked.mid) === value
        ? picked.url
        : text(props.previewUrl);
    // « Aucun site » se dit `null` à la médiathèque. Le YAML, lui, sème une
    // chaîne vide à la création d'un site (il n'a pas encore de gid) : sans
    // cette normalisation, le rail de la pop-in se croirait filtré sur un site
    // sans nom, et n'y marquerait plus la portée courante.
    const rawSiteId = resolve(props.siteId);
    const siteId = rawSiteId === '' || rawSiteId === undefined ? null : rawSiteId;
    const siteName = text(props.siteName);
    const title = text(props.title) || 'Médiathèque';
    const buttonLabel = text(props.buttonLabel) || 'Choisir une image';
    const emptyText = text(props.emptyText) || 'Aucune image';
    const previewAlt = text(props.previewAlt) || 'Image sélectionnée';
    const inputId = text(props.inputId) || undefined;
    const labelledBy = text(props.labelledBy);
    const describedBy = text(props.describedBy) || undefined;

    const write = (next) => globalDataContext?.updateData?.(next, formDataPath);

    return (
        <ActionDependant {...props}>
            <div className="flex items-center gap-3">
                {value && previewUrl ? (
                    <img
                        src={previewUrl}
                        alt={previewAlt}
                        className="h-12 w-12 rounded-md border border-slate-200 bg-white object-contain p-1"
                    />
                ) : (
                    <span
                        className="flex h-12 w-12 items-center justify-center rounded-md border border-dashed border-slate-300 bg-white text-slate-400"
                        aria-hidden="true"
                    >
                        {/* Décoratif : l'état « rien de choisi » est déjà dit par
                            le libellé du bouton, juste à côté. */}
                        —
                    </span>
                )}
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        id={inputId}
                        className="btn btn-secondary btn-sm"
                        aria-labelledby={labelledBy && inputId ? `${labelledBy} ${inputId}` : undefined}
                        aria-describedby={describedBy}
                        onClick={() => setOpen(true)}
                    >
                        {value ? 'Remplacer' : buttonLabel}
                    </button>
                    {value ? (
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => { setPicked(null); write(''); }}
                        >
                            Retirer
                        </button>
                    ) : (
                        <span className="text-xs text-slate-400">{emptyText}</span>
                    )}
                </div>
            </div>

            {open && (
                <MediaLibrary
                    value={value ? { mid: Number(value) } : null}
                    title={title}
                    siteId={siteId ?? null}
                    siteName={siteName}
                    onClose={() => setOpen(false)}
                    onPick={(item) => {
                        setPicked({ mid: item.mid, url: item.url });
                        write(String(item.mid));
                    }}
                />
            )}
        </ActionDependant>
    );
};
